import { useState, useCallback, useEffect } from 'react'
import type { CardLevel } from './types/card'
import { useDeck } from './hooks/useDeck'
import { isSupabaseAvailable } from './lib/supabase'
import { reviewCard } from './utils/srs'
import FlashCard from './components/FlashCard'
import Dashboard from './components/Dashboard'
import GojuonModal from './components/GojuonModal'

const LEVEL_OPTIONS: { value: CardLevel; label: string }[] = [
  { value: 'Gojuon', label: '🔤 五十音图' },
  { value: 'N5', label: '🌱 N5 核心词' },
  { value: 'N4', label: '🌿 N4 进阶词' },
  { value: 'N3', label: '🔥 N3 冲刺词' },
  { value: 'N2', label: '⚡ N2 高频词' },
  { value: 'N1', label: '👹 N1 魔鬼词' },
]

function App() {
  const [level, setLevel] = useState<CardLevel>('Gojuon')
  const {
    dueCards,
    allCards,
    masteredCount,
    newCount,
    levelTotal,
    isLevelComplete,
    updateCard,
    unlockNextBatch,
    aiGenerating,
    aiGenError,
    refresh,
    loading,
    error,
  } = useDeck(level)
  const [index, setIndex] = useState(0)
  const [flipped, setFlipped] = useState(false)
  const [showGojuon, setShowGojuon] = useState(false)

  // dueCards 减少时防止 index 越界
  useEffect(() => {
    if (index > 0 && index >= dueCards.length) {
      setIndex(Math.max(0, dueCards.length - 1))
    }
  }, [dueCards.length, index])

  const setLevelAndReset = (newLevel: CardLevel) => {
    setLevel(newLevel)
    setIndex(0)
    setFlipped(false)
  }

  const currentCard = dueCards[index] ?? null

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % dueCards.length)
    setFlipped(false)
  }, [dueCards.length])

  const handleRate = useCallback(
    (rating: 'forgot' | 'hard' | 'easy') => {
      try {
        if (!currentCard) return
        updateCard(reviewCard(currentCard, rating))
        goNext()
      } catch (e) {
        console.error('handleRate 执行异常:', e)
      }
    },
    [currentCard, updateCard, goNext]
  )

  const handleFlip = useCallback(() => {
    setFlipped((v) => !v)
  }, [])

  // 全局键盘快捷键
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      )
        return

      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault()
        handleFlip()
        return
      }

      if (flipped) {
        if (e.key === '1') handleRate('forgot')
        else if (e.key === '2') handleRate('hard')
        else if (e.key === '3') handleRate('easy')
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [flipped, handleFlip, handleRate])

  // 加载中
  if (loading) {
    return (
      <div className="min-h-svh flex flex-col items-center justify-center bg-zinc-950 text-white px-5">
        <div className="flex items-center gap-3 text-zinc-400">
          <span className="animate-spin inline-block w-5 h-5 border-2 border-white/20 border-t-white/60 rounded-full" />
          正在加载数据...
        </div>
      </div>
    )
  }

  // 云端配置缺失（环境变量未配置）
  if (error && !isSupabaseAvailable) {
    return (
      <div className="min-h-svh flex flex-col items-center justify-center bg-zinc-950 text-white px-5 gap-4">
        <div className="text-5xl mb-2">⚠️</div>
        <h2 className="text-xl font-bold text-red-400 text-center">
          《脚盆记》启动失败
        </h2>
        <p className="text-sm text-zinc-300 text-center max-w-xs leading-relaxed">
          检测到云端配置缺失，请确保 Vercel 环境变量已配置并重新部署。
        </p>
        <p className="text-[11px] text-zinc-500 text-center max-w-xs">
          需要配置的环境变量：VITE_SUPABASE_URL、VITE_SUPABASE_ANON_KEY
        </p>
      </div>
    )
  }

  // 加载出错（其他错误）
  if (error) {
    return (
      <div className="min-h-svh flex flex-col items-center justify-center bg-zinc-950 text-white px-5 gap-4">
        <p className="text-red-400 text-sm text-center">
          数据加载失败：{error}
        </p>
        <button
          onClick={refresh}
          className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-sm text-zinc-300 transition-colors"
        >
          重试
        </button>
      </div>
    )
  }

  // 牌库为空（云端无数据且 seed 未执行）
  if (allCards.length === 0) {
    return (
      <div className="min-h-svh flex flex-col items-center justify-center bg-zinc-950 text-white px-5 gap-4">
        <p className="text-zinc-400">牌库为空，请检查数据库连接</p>
        <button
          onClick={refresh}
          className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-sm text-zinc-300 transition-colors"
        >
          重试
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-svh flex flex-col items-center bg-zinc-950 pt-4 pb-8">
      {/* 错误提示横幅 */}
      {error && (
        <div className="w-full max-w-md mx-auto px-4 sm:px-5 mb-3">
          <div className="flex items-center justify-between gap-2 px-4 py-2 rounded-xl bg-red-600/20 border border-red-600/30 text-red-400 text-xs">
            <span>{error}</span>
            <button
              onClick={() => refresh()}
              className="shrink-0 px-2 py-1 rounded-lg bg-red-600/30 hover:bg-red-600/40 text-red-300 text-[11px] transition-colors"
            >
              重试
            </button>
          </div>
        </div>
      )}

      {/* 顶部栏 */}
      <div className="w-full max-w-md mx-auto px-4 sm:px-5 mb-1 flex items-center justify-between">
        <h1 className="text-base font-bold text-white tracking-wide">
          🇯🇵 脚盆记
        </h1>
        <button
          onClick={() => setShowGojuon(true)}
          className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-xs transition-all duration-150"
        >
          🗺️ 五十音总览
        </button>
      </div>

      {/* 数据看板 */}
      <Dashboard
        total={levelTotal}
        due={dueCards.length}
        mastered={masteredCount}
        newCount={newCount}
      />

      {/* 常驻预支新词挂件（智能变形） */}
      <div className="w-full max-w-md mx-auto px-4 sm:px-5 mt-2 flex flex-col items-end gap-1.5">
        {aiGenError && (
          <p className="text-red-400/80 text-xs text-center w-full">{aiGenError}</p>
        )}
        <button
          onClick={unlockNextBatch}
          disabled={aiGenerating}
          className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all duration-150 active:scale-95 ${
            aiGenerating
              ? 'bg-violet-500/15 border-violet-500/30 text-violet-300 cursor-wait'
              : newCount >= 5
                ? 'bg-amber-500/15 hover:bg-amber-500/25 border-amber-500/30 text-amber-300 hover:text-amber-200'
                : 'bg-violet-500/20 hover:bg-violet-500/30 border-violet-400/40 text-violet-200 hover:text-violet-100'
          }`}
        >
          {aiGenerating
            ? '⏳ AI 正在疯狂查阅字典...'
            : newCount >= 5
              ? `➕ 预支新词（剩${newCount}个）`
              : newCount > 0
                ? `➕ 预支新词（仅剩${newCount}个）`
                : '🤖 词库已空！让 AI 现编 5 个新词'}
        </button>
      </div>

      {/* 级别选择下拉菜单 */}
      <div className="mt-4 mx-4 sm:mx-5">
        <select
          value={level}
          onChange={(e) => setLevelAndReset(e.target.value as CardLevel)}
          className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:border-white/20 transition-colors hover:bg-white/8"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 12px center',
            paddingRight: '2.5rem',
          }}
        >
          {LEVEL_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-zinc-900 text-white">
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* 完美通关：该级别全部掌握 */}
      {isLevelComplete ? (
        <div className="flex-1 flex flex-col items-center justify-center text-white px-5 gap-4">
          <div className="text-6xl">🏆</div>
          <h1 className="text-2xl font-bold text-emerald-400">
            完美通关！
          </h1>
          <p className="text-sm text-zinc-400 text-center max-w-xs leading-relaxed">
            当前级别的所有卡片已全部掌握
          </p>
          <button
            onClick={unlockNextBatch}
            disabled={aiGenerating}
            className={`mt-2 px-6 py-3 rounded-xl text-white font-bold text-base shadow-lg transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-wait ${
              aiGenerating
                ? 'bg-gradient-to-r from-violet-500 to-purple-500 shadow-purple-500/25'
                : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 shadow-fuchsia-500/25'
            }`}
          >
            {aiGenerating
              ? '⏳ AI 正在疯狂查阅字典...'
              : '🤖 词库已空！让 AI 现编 5 个新词'}
          </button>
          {aiGenError && (
            <p className="text-red-400/80 text-xs text-center">{aiGenError}</p>
          )}
        </div>
      ) : dueCards.length === 0 || !currentCard ? (
        <div className="flex-1 flex flex-col items-center justify-center text-white px-5 gap-4">
          <div className="text-6xl mb-1">🎉</div>
          <h1 className="text-2xl font-semibold">当前级别下的复习已清空，太棒了！</h1>
          {newCount >= 5 ? (
            <>
              <p className="text-zinc-400 text-sm text-center">
                还有 {newCount} 个新词等你来挑战，随时可以继续
              </p>
              <button
                onClick={unlockNextBatch}
                disabled={aiGenerating}
                className="mt-2 px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold text-base shadow-lg shadow-orange-500/25 transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-wait"
              >
                🧠 劲头正足？继续学习 5 个新单词！
              </button>
            </>
          ) : (
            <>
              <p className="text-zinc-400 text-sm text-center">
                {newCount > 0
                  ? `只剩 ${newCount} 个新词，AI 可以帮你扩充词库`
                  : '本地词库已空，让 AI 为你现编新词'}
              </p>
              <button
                onClick={unlockNextBatch}
                disabled={aiGenerating}
                className={`mt-2 px-6 py-3 rounded-xl text-white font-bold text-base shadow-lg transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-wait ${
                  aiGenerating
                    ? 'bg-gradient-to-r from-violet-500 to-purple-500 shadow-purple-500/25'
                    : 'bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 shadow-fuchsia-500/25'
                }`}
              >
                {aiGenerating
                  ? '⏳ AI 正在疯狂查阅字典...'
                  : '🤖 词库已空！让 AI 现编 5 个新词'}
              </button>
              {aiGenError && (
                <p className="text-red-400/80 text-xs text-center">{aiGenError}</p>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center w-full">
          <div className="mb-4 text-xs text-zinc-500 tracking-widest uppercase">
            第 {index + 1} / {dueCards.length} 张
          </div>

          <FlashCard
            card={currentCard}
            flipped={flipped}
            onFlip={handleFlip}
            onRate={handleRate}
          />

          <div className="mt-6 flex gap-1.5">
            {[0, 1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full transition-colors ${
                  s <= (currentCard?.stage ?? 0)
                    ? 'bg-emerald-400'
                    : 'bg-white/10'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* 五十音总览弹窗 */}
      {showGojuon && <GojuonModal onClose={() => setShowGojuon(false)} />}
    </div>
  )
}

export default App
